import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Plus, Search, Pin, ChevronDown, ChevronRight } from 'lucide-react';
import type { FactoryRun } from '@/lib/types';
import { STAGE_LABELS, AWAITING_APPROVAL_STATUSES } from '@/lib/types';
import { stubNewThread } from './actions';

interface Props {
  activeRunId: string | null;
  onSelectRun: (runId: string) => void;
  pinned: FactoryRun[];
  all: FactoryRun[];
  archived: FactoryRun[];
  isMock: boolean;
  loading: boolean;
  error: string | null;
}

// Middle pane — the list of threads. Flat rows, no card borders, dividers
// between entries. PINNED surfaces runs awaiting founder approval; ALL is
// the full active set; ARCHIVED is collapsed by default.
export function ThreadList({
  activeRunId,
  onSelectRun,
  pinned,
  all,
  archived,
  isMock,
  loading,
  error,
}: Props) {
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const q = query.trim().toLowerCase();
  const matches = (r: FactoryRun) => {
    if (!q) return true;
    return (
      (r.customerName ?? '').toLowerCase().includes(q) ||
      (r.userEmail ?? '').toLowerCase().includes(q) ||
      (r.lastMessageSnippet ?? '').toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q)
    );
  };

  const filteredPinned = useMemo(() => pinned.filter(matches), [pinned, q]);
  const filteredAll = useMemo(() => all.filter(matches), [all, q]);
  const filteredArchived = useMemo(() => archived.filter(matches), [archived, q]);

  return (
    <section
      aria-label="Threads"
      className="flex h-full w-full flex-col border-r border-ink-100 bg-canvas-50/60 backdrop-blur-sm md:w-80"
    >
      <header className="flex flex-col gap-2 border-b border-ink-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink-900">Threads</h2>
          <button
            type="button"
            onClick={stubNewThread}
            className="inline-flex items-center gap-1 rounded-lg border border-ink-100 bg-white px-2 py-1 text-xs text-ink-700 transition hover:border-accent-300 hover:text-accent-700"
          >
            <Plus size={12} /> new
          </button>
        </div>
        <label className="flex items-center gap-2 rounded-lg border border-ink-100 bg-white px-2 py-1 text-sm text-ink-700 focus-within:border-accent-300">
          <Search size={14} className="text-ink-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer, email, or snippet…"
            className="w-full bg-transparent text-xs outline-none placeholder:text-ink-400"
          />
        </label>
        {isMock && (
          <div className="text-[10px] uppercase tracking-wider text-ink-400">
            mock data — no live Firestore connection
          </div>
        )}
        {error && (
          <div className="text-[10px] text-rose-600">
            subscription error: {error} — showing mocks
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <Section title="Pinned" count={filteredPinned.length} icon={<Pin size={12} />} defaultOpen>
          {filteredPinned.length === 0 ? (
            <EmptyRow text="No runs awaiting approval." />
          ) : (
            filteredPinned.map((r) => (
              <ThreadRow
                key={r.id}
                run={r}
                active={activeRunId === r.id}
                onClick={() => onSelectRun(r.id)}
                showApprovalBadge
              />
            ))
          )}
        </Section>

        <Section title="All" count={filteredAll.length} defaultOpen>
          {loading && all.length === 0 ? (
            <EmptyRow text="Loading threads…" />
          ) : filteredAll.length === 0 ? (
            <EmptyRow text="No active threads." />
          ) : (
            filteredAll.map((r) => (
              <ThreadRow
                key={r.id}
                run={r}
                active={activeRunId === r.id}
                onClick={() => onSelectRun(r.id)}
              />
            ))
          )}
        </Section>

        <Section
          title="Archived"
          count={filteredArchived.length}
          collapsible
          open={showArchived}
          onToggle={() => setShowArchived((v) => !v)}
        >
          {filteredArchived.length === 0 ? (
            <EmptyRow text="No archived threads." />
          ) : (
            filteredArchived.map((r) => (
              <ThreadRow
                key={r.id}
                run={r}
                active={activeRunId === r.id}
                onClick={() => onSelectRun(r.id)}
                muted
              />
            ))
          )}
        </Section>
      </div>
    </section>
  );
}

function Section({
  title,
  count,
  icon,
  children,
  defaultOpen = true,
  collapsible = false,
  open,
  onToggle,
}: {
  title: string;
  count: number;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = collapsible ? (open ?? internalOpen) : true;
  const toggle = () => {
    if (collapsible) {
      if (onToggle) onToggle();
      else setInternalOpen((v) => !v);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className={clsx(
          'sticky top-0 z-10 flex w-full items-center justify-between border-b border-ink-100/60 bg-canvas-50/90 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400 backdrop-blur',
          collapsible ? 'cursor-pointer hover:text-ink-600' : 'cursor-default',
        )}
        disabled={!collapsible}
      >
        <span className="flex items-center gap-1.5">
          {collapsible ? (
            isOpen ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )
          ) : (
            icon
          )}
          {title}
          <span className="rounded-full bg-ink-100/70 px-1.5 py-px text-[9px] text-ink-600">
            {count}
          </span>
        </span>
      </button>
      {isOpen && <div>{children}</div>}
    </div>
  );
}

function ThreadRow({
  run,
  active,
  onClick,
  showApprovalBadge,
  muted,
}: {
  run: FactoryRun;
  active?: boolean;
  onClick: () => void;
  showApprovalBadge?: boolean;
  muted?: boolean;
}) {
  const isAwaitingApproval =
    run.awaitingApproval || AWAITING_APPROVAL_STATUSES.includes(run.status);
  const displayName = run.customerName?.trim() || newCustomerLabel(run);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={clsx(
        'thread-row flex items-start gap-2',
        active && 'is-active',
        muted && 'opacity-60',
      )}
    >
      <div
        className={clsx(
          'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
          run.status === 'paused'
            ? 'bg-status-paused'
            : isAwaitingApproval
              ? 'bg-status-awaiting animate-pulse-soft'
              : run.status === 'complete'
                ? 'bg-status-complete'
                : 'bg-status-active',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div
            className={clsx(
              'truncate text-sm font-semibold text-ink-900',
              run.unreadForFounder && 'after:ml-1.5 after:inline-block after:h-2 after:w-2 after:rounded-full after:bg-accent-500 after:align-middle',
            )}
          >
            {displayName}
          </div>
          <span className="flex-shrink-0 text-[10px] text-ink-400">
            {relativeTime(run.updatedAtMs)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 pt-0.5">
          <span className="rounded bg-white px-1.5 py-px text-[9px] font-medium uppercase tracking-wider text-ink-600">
            {STAGE_LABELS[run.currentStage]}
          </span>
          {showApprovalBadge && (
            <span className="rounded bg-amber-100 px-1.5 py-px text-[9px] font-medium uppercase tracking-wider text-amber-900">
              needs approval
            </span>
          )}
        </div>
        {run.lastMessageSnippet && (
          <div className="mt-1 line-clamp-2 text-xs text-ink-600">{run.lastMessageSnippet}</div>
        )}
      </div>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="px-4 py-3 text-xs text-ink-400">{text}</div>;
}

function newCustomerLabel(run: FactoryRun): string {
  const n = run.userEmail?.split('@')[0] ?? run.id.slice(-4);
  return `New Customer · ${n}`;
}

function relativeTime(ms?: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'now';
  if (diff < hour) return `${Math.floor(diff / min)}m`;
  if (diff < day) return `${Math.floor(diff / hour)}h`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
  return new Date(ms).toLocaleDateString();
}
